import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { setSelectedWorkspace } from "../store/slices/workspaceSlice";

const SYNCED_ROUTES = [
  "/links",
  "/notes",
  "/workspace",
  "/threads",
  "/browse",
  "/about",
];

function parsePath(
  pathname: string,
): { base: string; workspaceId: string | null } | null {
  for (const base of SYNCED_ROUTES) {
    if (pathname === base) return { base, workspaceId: null };
    if (pathname.startsWith(base + "/")) {
      const rest = pathname.slice(base.length + 1);
      const segment = rest.split("/")[0];
      return { base, workspaceId: segment || null };
    }
  }
  return null;
}

export default function WorkspaceUrlSync() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const workspaces = useAppSelector((state) => state.workspace.workspaces);
  const allWorkspaces = useAppSelector(
    (state) => state.workspace.allWorkspaces,
  );
  const selectedWorkspace = useAppSelector(
    (state) => state.workspace.selectedWorkspace,
  );

  const lastSyncedPathnameRef = useRef<string | null>(null);
  const initialAdoptionDoneRef = useRef(false);
  // The workspace the initial adoption selected from the URL, until that
  // selection reaches this component; Redux -> URL holds off meanwhile.
  const adoptingIdRef = useRef<string | null>(null);

  // Until the workspace list is here (from the cache or the API) the URL's
  // workspace can't be matched, so the URL is left alone: a cold load of
  // /links/5 keeps its 5 for the initial adoption instead of being rewritten
  // to the stored selection first.
  const listReady = allWorkspaces.length > 0 || workspaces.length > 0;

  const workspacesRef = useRef(workspaces);
  const allWorkspacesRef = useRef(allWorkspaces);
  const selectedWorkspaceRef = useRef(selectedWorkspace);
  // Keep the refs current. Declared before the effects that read them, so it
  // runs first in each commit.
  useEffect(() => {
    workspacesRef.current = workspaces;
    allWorkspacesRef.current = allWorkspaces;
    selectedWorkspaceRef.current = selectedWorkspace;
  });

  // URL -> Redux: fires only when pathname actually changes (direct navigation, back/forward,
  // nav link click). Reads workspace data from refs so this effect does NOT re-run when the
  // workspace list mutates (which would race against user dropdown selections).
  useEffect(() => {
    if (lastSyncedPathnameRef.current === location.pathname) return;
    lastSyncedPathnameRef.current = location.pathname;

    const parsed = parsePath(location.pathname);
    if (!parsed || !parsed.workspaceId) return;

    const pool = allWorkspacesRef.current.length
      ? allWorkspacesRef.current
      : workspacesRef.current;
    if (pool.length === 0) return; // handled by the initial-adoption effect below

    const match = pool.find((ws) => String(ws.id) === parsed.workspaceId);
    if (!match) return;

    const current = selectedWorkspaceRef.current;
    const currentId = current?.id ?? current;
    if (String(currentId) !== parsed.workspaceId) {
      dispatch(setSelectedWorkspace(match));
    }
  }, [location.pathname, dispatch]);

  // Initial adoption: when the app mounts with /links/5 but workspaces aren't loaded yet,
  // run URL->Redux once after the first workspace list arrives. initialAdoptionDoneRef
  // enforces the "once" — any extra runs triggered by the deps below just early-return.
  useEffect(() => {
    if (initialAdoptionDoneRef.current) return;

    const pool = allWorkspaces.length ? allWorkspaces : workspaces;
    if (pool.length === 0) return;

    initialAdoptionDoneRef.current = true;

    const parsed = parsePath(location.pathname);
    if (!parsed || !parsed.workspaceId) return;

    const match = pool.find((ws) => String(ws.id) === parsed.workspaceId);
    if (!match) return;

    const currentId = selectedWorkspace?.id ?? selectedWorkspace;
    if (String(currentId) !== parsed.workspaceId) {
      lastSyncedPathnameRef.current = location.pathname;
      adoptingIdRef.current = parsed.workspaceId;
      dispatch(setSelectedWorkspace(match));
    }
  }, [workspaces, allWorkspaces, location.pathname, selectedWorkspace, dispatch]);

  // Redux -> URL: when selectedWorkspace changes, update the URL segment,
  // once the list has loaded.
  useEffect(() => {
    if (!listReady) return;
    const parsed = parsePath(location.pathname);
    if (!parsed) return;

    const selectedId = selectedWorkspace?.id ?? selectedWorkspace;
    const selectedIdStr = selectedId != null ? String(selectedId) : null;

    // The adoption above just selected the URL's workspace; this render still
    // has the selection from before it.
    if (adoptingIdRef.current !== null) {
      if (selectedIdStr !== adoptingIdRef.current) return;
      adoptingIdRef.current = null;
    }

    if (selectedIdStr === parsed.workspaceId) return;

    const newPath = selectedIdStr
      ? `${parsed.base}/${selectedIdStr}`
      : parsed.base;

    lastSyncedPathnameRef.current = newPath;
    navigate(newPath + location.search + location.hash, { replace: true });
  }, [
    listReady,
    selectedWorkspace,
    location.pathname,
    location.search,
    location.hash,
    navigate,
  ]);

  return null;
}
